import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';

export interface OrganizationSubmission {
  name: string;
  type?: string;
  kind: 'submit' | 'claim';
  submitted_at: string;
}

/**
 * Remembers that the user submitted or claimed an organization, so `/onboarding`
 * can show "under review" instead of the create/claim form.
 *
 * `POST users/organizations/submit` queues the organization for admin review and
 * grants no membership, and there is no endpoint that reports pending
 * submissions — hence a local marker.
 *
 * **This never gates anything.** The gate is `user.organizations.length`, which
 * is server truth; the marker only chooses which copy to render. That
 * containment is what makes a client-side record acceptable here.
 *
 * Known limits: it is per browser profile (another device shows the form again,
 * where the backend's duplicate-name validation is the real backstop), and an
 * admin *rejecting* the submission is undetectable — so the pending state always
 * offers a way to submit something else.
 */
@Injectable({ providedIn: 'root' })
export class OrganizationSubmissionService {
  private platformId = inject(PLATFORM_ID);

  private storageKey(userId: string): string {
    return `riterz:org-submission:${userId}`;
  }

  record(userId: string, submission: OrganizationSubmission): void {
    if (!isPlatformBrowser(this.platformId) || !userId) { return; }
    try {
      localStorage.setItem(this.storageKey(userId), JSON.stringify(submission));
    } catch {
      // Private mode / storage disabled — onboarding just shows the form again.
    }
  }

  get(userId: string | null | undefined): OrganizationSubmission | null {
    if (!isPlatformBrowser(this.platformId) || !userId) { return null; }
    try {
      const raw = localStorage.getItem(this.storageKey(userId));
      if (!raw) { return null; }

      const parsed = JSON.parse(raw) as Partial<OrganizationSubmission>;
      if (typeof parsed?.name !== 'string' || !parsed.name) { return null; }

      return {
        name: parsed.name,
        type: typeof parsed.type === 'string' ? parsed.type : undefined,
        kind: parsed.kind === 'claim' ? 'claim' : 'submit',
        submitted_at: typeof parsed.submitted_at === 'string' ? parsed.submitted_at : '',
      };
    } catch {
      return null;
    }
  }

  clear(userId: string | null | undefined): void {
    if (!isPlatformBrowser(this.platformId) || !userId) { return; }
    try {
      localStorage.removeItem(this.storageKey(userId));
    } catch {
      // Ignore — nothing to clean up.
    }
  }
}
